/**
 * The Trigger Trace section's own vocabulary, mapped onto the panel-wide design
 * tokens in component/common/designSystem/theme.js — the same split
 * cessationTheme.js and scanTheme.js use: only domain meaning lives here,
 * palettes belong to the design system.
 */
import { CHART_COLORS } from '../../../../component/common/designSystem';

/**
 * The three states a log can be in, and what this section calls them.
 *
 * The database says 'failed'. Nothing in this section does. It is the person's
 * own honest mark that they did not get past something, entered privately for
 * their own benefit, and an employer reading it as a failure would be reading
 * it wrong. 'Still open' likewise: an unresolved trigger is unfinished, not
 * neglected.
 */
export const STATUS_LABELS = {
  resolved: 'Worked through',
  failed: 'Could not shake it',
  pending: 'Still open'
};

/**
 * Green for worked through, amber for could not shake it, grey for still open.
 *
 * Amber rather than red on purpose: red would read as a failure state, and the
 * one thing this section must not do is turn somebody's honest "I could not get
 * past this" into a mark against them or their team.
 */
export const STATUS_RAMP = [CHART_COLORS.greenDeep, CHART_COLORS.amber, CHART_COLORS.gray];

/**
 * One fixed hue per area of life, so 'Work' is the same colour on every chart
 * and on every scope. Work gets indigo — the panel's primary — because it is
 * the area this audience is actually here about, not because it is the worst.
 * None of these hues carries a verdict.
 */
export const AREA_COLORS = {
  Work: CHART_COLORS.brand,
  Relationship: CHART_COLORS.pink,
  Family: CHART_COLORS.orange,
  Financial: CHART_COLORS.teal,
  Health: CHART_COLORS.green,
  Social: CHART_COLORS.blue,
  Other: CHART_COLORS.moss,
  'Not recorded': CHART_COLORS.gray
};

/** The order life areas appear in, matching the backend's catalogue. */
export const AREA_ORDER = [
  'Work',
  'Relationship',
  'Family',
  'Financial',
  'Health',
  'Social',
  'Other',
  'Not recorded'
];

export const AREA_RAMP = AREA_ORDER.map((label) => AREA_COLORS[label]);
export const areaColor = (label) => AREA_COLORS[label] || CHART_COLORS.gray;

/**
 * How hard it hit — mild through severe. Green to red, because this is a
 * reading on the size of the thing that happened, not on the person it
 * happened to.
 */
export const INTENSITY_RAMP = [
  CHART_COLORS.green,
  CHART_COLORS.teal,
  CHART_COLORS.amber,
  CHART_COLORS.orange,
  CHART_COLORS.red
];

/**
 * How long from logging something to marking it worked through. Climbs from
 * green at 'same day' — quicker is genuinely better here, unlike most of the
 * scales in this panel.
 */
export const RESOLVE_RAMP = [
  CHART_COLORS.greenDeep,
  CHART_COLORS.green,
  CHART_COLORS.teal,
  CHART_COLORS.amber,
  CHART_COLORS.orange
];

/**
 * How long the still-open ones have been sitting. Runs the other way: an
 * untouched log from three months ago is the one worth noticing.
 */
export const BACKLOG_RAMP = [
  CHART_COLORS.teal,
  CHART_COLORS.amber,
  CHART_COLORS.orange,
  CHART_COLORS.red
];

/** Ranked lists — triggers, emotions. No verdict, just order. */
export const RANK_RAMP = [
  CHART_COLORS.brand,
  CHART_COLORS.blue,
  CHART_COLORS.teal,
  CHART_COLORS.greenDeep,
  CHART_COLORS.amber,
  CHART_COLORS.orange,
  CHART_COLORS.pink,
  CHART_COLORS.moss,
  CHART_COLORS.brandSoft
];

/** How much somebody logs, and how many days they showed up. */
export const VOLUME_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep
];

/** How long the emotional shift lasted, shortest to longest. */
export const DURATION_RAMP = [
  CHART_COLORS.green,
  CHART_COLORS.teal,
  CHART_COLORS.blue,
  CHART_COLORS.amber,
  CHART_COLORS.orange,
  CHART_COLORS.gray
];
